import _find from 'lodash/find';
import _indexOf from 'lodash/indexOf';
import _isEmpty from 'lodash/isEmpty';
import _isNaN from 'lodash/isNaN';
import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import _parseInt from 'lodash/parseInt';
import _split from 'lodash/split';
import _toString from 'lodash/toString';
import _values from 'lodash/values';
// Serialization
import { deserialize, serialize } from 'serializr';
import { asyncOperationSchema } from './schemas';
// Color
import { iconPalette, statusPalette } from '../components/core/colors';
// Models
import mDevice from './device';
// Custom
import {
  ASYNC_OP_STATUS_PENDING, ASYNC_OP_STATUS_SUCCESS, ASYNC_OP_STATUS_ERROR,
  ASYNC_OP_STATUS_ABORTED, ASYNC_OP_STATUS_IGNORED,
} from '../constants/App';
import { numberTo2DigitsString } from '../utils/Tools';

/**
* Serialization
*/
const deserializeAsyncOperation = (data) => deserialize(asyncOperationSchema, data);

const serializeAsyncOperation = ({
  id, status, location, devices,
}) => ({
  ...serialize(asyncOperationSchema, {
    id, status, location, devices,
  }),
});

/**
* Utils
*/
// --- Operations --- //
const extractIdFromLocation = (location, resource) => {
  if (!location || !resource) {
    return null;
  }
  // Retrieve id from location
  const splitLocation = _split(location, '/');
  const resourceIndex = _indexOf(splitLocation, resource);
  if (resourceIndex === -1 || splitLocation.length <= resourceIndex + 1) {
    return null;
  }
  return splitLocation[resourceIndex + 1];
};

const createAsyncOperation = (serverAsyncOperation, opts) => {
  // Basic checks
  if (!serverAsyncOperation || !opts || !opts.key) {
    return null;
  }
  // Retrive id, either directly (if update/delete), or from location sent by server (add)
  const idObj = _parseInt(opts.id || extractIdFromLocation(serverAsyncOperation.location, opts.resource));
  if (!idObj || _isNaN(idObj)) {
    return null;
  }
  // Create async operation based on server information
  const {
    id, status, location, devices,
  } = deserializeAsyncOperation(serverAsyncOperation);
  const asyncOperation = {
    ...serializeAsyncOperation({
      id, status, location, devices,
    }),
    targetKey: opts.key,
    [opts.key]: idObj,
  };

  return asyncOperation;
};

// --- Status --- //
/*
Converts sync_status sent by server into an array:
{
  device_id_1: async_operation_id_1,
  device_id_2: async_operation_id_2
}
becomes
[
  {device_id: device_id_1, async_operation_id: async_operation_id_1},
  {device_id: device_id_2, async_operation_id: async_operation_id_2}
]
*/
const syncStatusToDevicesSyncStatus = (syncStatus) => _values(
  _mapValues(syncStatus, (value, key) => ({ device_id: _parseInt(key), async_operation_id: _parseInt(value) })),
);

const asyncStatusFromBool = (sync) => (sync ? ASYNC_OP_STATUS_SUCCESS : ASYNC_OP_STATUS_ERROR);

const asyncStatusComputedWithDevice = (asyncStatus, device) => ((device && mDevice.isDisabled(device)) ? ASYNC_OP_STATUS_IGNORED : asyncStatus);

const asyncStatusLabel = (status, code) => {
  if (
    status !== ASYNC_OP_STATUS_PENDING
    && status !== ASYNC_OP_STATUS_ABORTED
    && status !== ASYNC_OP_STATUS_IGNORED
  ) {
    if (code == null) {
      return `asyncOperation.responseCode.${numberTo2DigitsString(-1)}`;
    }
    return `asyncOperation.responseCode.${numberTo2DigitsString(code)}`;
  }
  return `syncStatus.${status}`;
};

const asyncStatus = (asyncOperation, resouce, devices) => {
  const { sync_status: resourceSyncStatus, async_operation_id: resourceAsyncOperationId, is_synchronized: resourceIsSynchronized } = resouce;

  // Default status
  let globalStatus = asyncStatusFromBool(resourceIsSynchronized);
  let devicesStatus;
  if (!asyncOperation) {
    // No async operation in progress => determine status from server data
    const syncStatusDevices = syncStatusToDevicesSyncStatus(resourceSyncStatus);
    if (!_isEmpty(syncStatusDevices)) {
      // Parse sync status
      // Each element is an object { device_id: xx, async_operation_id: yy }
      let deviceStatus;
      devicesStatus = _map(syncStatusDevices, ({ device_id: deviceId, async_operation_id: asyncOperationId }) => {
        deviceStatus = asyncStatusFromBool(asyncOperationId >= resourceAsyncOperationId);
        // The device
        const device = _find(devices, ['id', deviceId]);
        // Ignore status for this device if it is disabled
        deviceStatus = asyncStatusComputedWithDevice(deviceStatus, device);
        // Create object for this device
        return {
          key: _toString(deviceId),
          device,
          status: deviceStatus,
          statusText: `syncStatus.${deviceStatus}`,
        };
      });
    }
    // Devices are not loaded => do not display devices status
    if (_isEmpty(devices)) {
      devicesStatus = null;
    }
  } else {
    // If an async operation is in progress, take its status
    globalStatus = asyncOperation.status;
    // If devices are loaded, determine status for each device
    if (devices && !_isEmpty(devices)) {
      devicesStatus = _map(asyncOperation.devices, (d) => (
        {
          key: _toString(d.device_id),
          device: _find(devices, ['id', d.device_id]),
          status: d.ignored ? ASYNC_OP_STATUS_IGNORED : d.status,
          statusText: asyncStatusLabel(d.ignored ? ASYNC_OP_STATUS_IGNORED : d.status, d.code),
        }
      ));
    }
  }
  // Global status desc
  const globalStatusDesc = `syncStatus.${globalStatus}`;

  return { globalStatus, globalStatusDesc, devicesStatus };
};

const asyncStatusIcon = (status) => {
  let icon;
  let color;
  switch (status) {
    case ASYNC_OP_STATUS_PENDING:
      icon = 'asyncStatusPending';
      color = statusPalette.pending;
      break;
    case ASYNC_OP_STATUS_SUCCESS:
      icon = 'asyncStatusSuccess';
      color = statusPalette.success;
      break;
    case ASYNC_OP_STATUS_ERROR:
      icon = 'asyncStatusWarning';
      color = statusPalette.warning;
      break;
    case ASYNC_OP_STATUS_ABORTED:
      icon = 'itemNothing';
      color = statusPalette.aborted;
      break;
    case ASYNC_OP_STATUS_IGNORED:
      icon = 'itemNothing';
      color = statusPalette.ignored;
      break;
    default:
      icon = 'itemNothing';
      color = iconPalette.base;
      break;
  }
  return { icon, color };
};

const asyncMonoDeviceResultDesc = (asyncOperation) => {
  const asyncOperationDevice = asyncOperation.devices[0];
  return `asyncOperation.responseCode.${numberTo2DigitsString(asyncOperationDevice.code)}`;
};

/**
* Model
*/
const async = {
  // Name
  name: 'async',

  // Utils
  createAsyncOperation,
  status: asyncStatus,
  statusComputedWithDevice: asyncStatusComputedWithDevice,
  statusLabel: asyncStatusLabel,
  statusIcon: asyncStatusIcon,
  monoDeviceResultDesc: asyncMonoDeviceResultDesc,
  statusFromBool: asyncStatusFromBool,
};

module.exports = async;



// WEBPACK FOOTER //
// ./models/async.js